import type { InteractionSubmitPayload } from "../axios/interaction";
import type {
  AssessmentAnswerItem,
  AssessmentAnswerPayload,
  AssessmentQuestionAnswerBlock,
  AssessmentQuestion,
  Answer,
  AssessmentQuestionType,
} from "../axios/quiz";
import {
  DEFAULT_ASSESSMENT_QUESTION_TYPE,
  normalizeAssessmentQuestionType,
} from "../axios/quiz";
import type {
  ContentBlock,
  FillInBlankContentBlock,
  MatchingContentBlock,
  OrderingContentBlock,
  OrderedResponseContentBlock,
} from "../types";

export const INTERACTIVE_ASSESSMENT_TYPES = [
  "fill_in_blank",
  "ordering",
  "matching",
  "ordered_response",
] as const;

export type InteractiveAssessmentType =
  (typeof INTERACTIVE_ASSESSMENT_TYPES)[number];

export const isInteractiveAssessmentType = (
  questionType: AssessmentQuestionType | undefined,
): questionType is InteractiveAssessmentType =>
  INTERACTIVE_ASSESSMENT_TYPES.includes(
    questionType as InteractiveAssessmentType,
  );

const resolveAssessmentQuestionType = (
  raw: Record<string, unknown>,
): AssessmentQuestionType =>
  normalizeAssessmentQuestionType(raw.type ?? raw.question_type) ??
  DEFAULT_ASSESSMENT_QUESTION_TYPE;

export const getAssessmentQuestionType = (
  question: Pick<AssessmentQuestion, "type" | "question_type">,
): AssessmentQuestionType =>
  question.type ??
  question.question_type ??
  DEFAULT_ASSESSMENT_QUESTION_TYPE;

export const isMsqAssessmentType = (
  questionType: AssessmentQuestionType | undefined,
): boolean =>
  !questionType || questionType === "msq";

export const isOpenTextAssessmentType = (
  questionType: AssessmentQuestionType | undefined,
): boolean => questionType === "open_text";

const formatMatchingQuestion = (item: Record<string, unknown>): string => {
  const title = typeof item.title === "string" ? item.title.trim() : "";
  const instruction =
    typeof item.instruction === "string" ? item.instruction.trim() : "";

  const leftItems = Array.isArray(item.left) ? item.left : [];
  const leftTexts = leftItems
    .filter(
      (left): left is Record<string, unknown> =>
        Boolean(left) && typeof left === "object",
    )
    .map((left) =>
      typeof left.text === "string" ? left.text.trim() : "",
    )
    .filter(Boolean);

  const questionParts: string[] = [];

  if (title) {
    questionParts.push(title);
  }

  if (instruction) {
    questionParts.push(instruction);
  }

  if (leftTexts.length > 0) {
    questionParts.push("Items to match:");
    leftTexts.forEach((text, index) => {
      questionParts.push(`${index + 1}. ${text}`);
    });
  }

  return questionParts.join("\n").trim();
};

const formatFillInBlankQuestion = (item: Record<string, unknown>): string =>
  typeof item.template === "string" ? item.template : "";

const formatOrderingQuestion = (item: Record<string, unknown>): string =>
  typeof item.prompt === "string" ? item.prompt : "";

const extractFormattedInteractiveQuestion = (
  item: Record<string, unknown>,
  itemType: string,
): string => {
  switch (itemType) {
    case "matching":
      return formatMatchingQuestion(item);
    case "fill_in_blank":
      return formatFillInBlankQuestion(item);
    case "ordering":
    case "ordered_response":
      return formatOrderingQuestion(item);
    default:
      return "";
  }
};

// Mirrors backend Assessment.extract_question()
export const extractAssessmentQuestion = (
  item: Record<string, unknown> | string,
): string => {
  if (typeof item === "string") {
    return item;
  }

  const itemType = item.type ?? item.question_type;
  if (typeof itemType === "string") {
    const formatted = extractFormattedInteractiveQuestion(item, itemType);
    if (formatted) {
      return formatted;
    }
  }

  const rawQuestion = item.question;
  if (typeof rawQuestion === "string" && rawQuestion) {
    return rawQuestion;
  }

  if (typeof itemType === "string") {
    return extractFormattedInteractiveQuestion(item, itemType);
  }

  return "";
};

export const resolveAssessmentQuestionText = (
  question: Pick<
    AssessmentQuestion,
    | "question"
    | "title"
    | "prompt"
    | "template"
    | "type"
    | "question_type"
    | "instruction"
    | "left"
  >,
): string =>
  extractAssessmentQuestion({
    question: question.question,
    type: question.type ?? question.question_type,
    question_type: question.question_type ?? question.type,
    title: question.title,
    instruction: question.instruction,
    prompt: question.prompt,
    template: question.template,
    left: question.left,
  });

export const assessmentQuestionToRaw = (
  question: AssessmentQuestion,
): Record<string, unknown> => {
  const questionType = getAssessmentQuestionType(question);
  return {
    question: question.question,
    type: questionType,
    question_type: questionType,
    title: question.title,
    instruction: question.instruction,
    prompt: question.prompt,
    template: question.template,
    ...(question.answers !== undefined ? { answers: question.answers } : {}),
    ...(question.options ? { options: question.options } : {}),
    ...(question.blanks ? { blanks: question.blanks } : {}),
    ...(question.left ? { left: question.left } : {}),
    ...(question.right ? { right: question.right } : {}),
  };
};

export const resolveAssessmentQuestionKey = (
  question: AssessmentQuestion,
): string => extractAssessmentQuestion(assessmentQuestionToRaw(question));

export type DashboardQuestionRef =
  | string
  | Record<string, unknown>
  | null
  | undefined;

export const formatAssessmentQuestionIndexLabel = (
  questionIndex: number,
): string => `Q${questionIndex + 1}`;

export const resolveDashboardQuestionIndex = (
  ref: DashboardQuestionRef,
  fallbackIndex?: number,
): number | undefined => {
  if (ref && typeof ref === "object" && typeof ref.question_index === "number") {
    return ref.question_index;
  }
  if (typeof fallbackIndex === "number" && fallbackIndex >= 0) {
    return fallbackIndex;
  }
  return undefined;
};

// Backend may return question as a string or full question object in dashboard payloads.
export const resolveDashboardQuestionKey = (
  ref: DashboardQuestionRef,
): string => {
  if (typeof ref === "string") {
    return ref;
  }
  if (!ref || typeof ref !== "object") {
    return "";
  }
  return extractAssessmentQuestion(ref);
};

export type QuestionPreviewSegment = {
  text: string;
  bold?: boolean;
};

const PREVIEW_PART_BREAK: QuestionPreviewSegment = { text: "\n\n" };

const appendPlainPreviewPart = (
  segments: QuestionPreviewSegment[],
  text: string,
) => {
  const trimmed = text.trim();
  if (!trimmed) {
    return;
  }
  if (segments.length) {
    segments.push(PREVIEW_PART_BREAK);
  }
  segments.push({ text: trimmed });
};

const readQuestionField = (item: Record<string, unknown>, key: string): string =>
  typeof item[key] === "string" ? item[key].trim() : "";

const formatOptionListPreview = (
  label: string,
  raw: unknown,
): string => {
  if (!Array.isArray(raw)) {
    return "";
  }

  const lines = raw
    .filter((item) => item && typeof item === "object")
    .map((item) => String((item as Record<string, unknown>).text ?? "").trim())
    .filter(Boolean)
    .map((text) => `• ${text}`);

  if (!lines.length) {
    return "";
  }

  return `${label}:\n${lines.join("\n")}`;
};

export const normalizeAssessmentQuestion = (
  raw: Record<string, unknown>,
  arrayIndex?: number,
): AssessmentQuestion => {
  const question_type = resolveAssessmentQuestionType(raw);
  const rawQuestionText =
    typeof raw.question === "string"
      ? raw.question
      : typeof raw.text === "string"
        ? raw.text
        : "";
  const question = isInteractiveAssessmentType(question_type)
    ? rawQuestionText
    : extractAssessmentQuestion(raw);

  const result: AssessmentQuestion = {
    question,
    type: question_type,
    question_type,
    competency: typeof raw.competency === "string" ? raw.competency : "",
    language: typeof raw.language === "string" ? raw.language : "english",
    grading_guidance:
      typeof raw.grading_guidance === "string" ? raw.grading_guidance : "",
    scoring_rubric:
      raw.scoring_rubric &&
      typeof raw.scoring_rubric === "object" &&
      !Array.isArray(raw.scoring_rubric)
        ? (raw.scoring_rubric as NonNullable<
            AssessmentQuestion["scoring_rubric"]
          >)
        : {
            advanced: "",
            professional: "",
            expert: "",
          },
  };

  if (typeof raw.id === "string" && raw.id.trim()) {
    result.id = raw.id.trim();
  } else if (typeof raw.question_id === "string" && raw.question_id.trim()) {
    result.id = raw.question_id.trim();
  }

  if (raw.answers !== undefined) {
    result.answers = raw.answers as AssessmentQuestion["answers"];
  }
  if (typeof raw.max_score === "number") {
    result.max_score = raw.max_score;
  }
  if (typeof raw.title === "string") {
    result.title = raw.title;
  }
  if (typeof raw.instruction === "string") {
    result.instruction = raw.instruction;
  }
  if (typeof raw.prompt === "string") {
    result.prompt = raw.prompt;
  }
  if (typeof raw.template === "string") {
    result.template = raw.template;
  }
  if (Array.isArray(raw.blanks)) {
    result.blanks = raw.blanks as AssessmentQuestion["blanks"];
  }
  if (Array.isArray(raw.options)) {
    result.options = raw.options as AssessmentQuestion["options"];
  }
  if (typeof raw.slot_count === "number") {
    result.slot_count = raw.slot_count;
  }
  if (Array.isArray(raw.left)) {
    result.left = raw.left as AssessmentQuestion["left"];
  }
  if (Array.isArray(raw.right)) {
    result.right = raw.right as AssessmentQuestion["right"];
  }

  const question_index =
    typeof raw.question_index === "number"
      ? raw.question_index
      : typeof arrayIndex === "number"
        ? arrayIndex
        : undefined;
  if (question_index !== undefined) {
    result.question_index = question_index;
  }

  const isBonus = raw.is_bonus === true;
  result.is_bonus = isBonus;
  result.question_category =
    raw.question_category === "bonus" || isBonus ? "bonus" : "mandatory";

  return result;
};

const normalizeOptions = (
  raw: unknown,
): Array<{ id: string; text: string }> => {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((item) => item && typeof item === "object")
    .map((item) => {
      const option = item as Record<string, unknown>;
      return {
        id: String(option.id ?? "").trim(),
        text: String(option.text ?? "").trim(),
      };
    })
    .filter((option) => option.id && option.text);
};

const normalizeBlanks = (
  raw: unknown,
): Array<{ id: string; label?: string }> => {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((item) => item && typeof item === "object")
    .map((item) => {
      const blank = item as Record<string, unknown>;
      return {
        id: String(blank.id ?? "").trim(),
        ...(typeof blank.label === "string" ? { label: blank.label } : {}),
      };
    })
    .filter((blank) => blank.id);
};

const normalizeStringArray = (raw: unknown): string[] | undefined => {
  if (!Array.isArray(raw)) return undefined;
  const values = raw.map(String).filter(Boolean);
  return values.length > 0 ? values : undefined;
};

const normalizeAnswersMap = (raw: unknown): Record<string, string> => {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  return Object.entries(raw as Record<string, unknown>).reduce<
    Record<string, string>
  >((acc, [leftId, rightId]) => {
    const left = String(leftId ?? "").trim();
    const right = String(rightId ?? "").trim();
    if (left && right) acc[left] = right;
    return acc;
  }, {});
};

const FILL_IN_BLANK_TEMPLATE_TOKEN = /\{\{(\w+)\}\}/g;

const resolveFillInBlankInsertedText = (
  answerValue: string,
  optionById: Map<string, string>,
): string => {
  const trimmed = answerValue.trim();
  if (!trimmed) return "";
  return optionById.get(trimmed) ?? trimmed;
};

const resolveBlankFallbackLabel = (
  blankId: string,
  blanks: Array<{ id: string; label?: string }>,
): string => {
  const blank = blanks.find((item) => item.id === blankId);
  const label = blank?.label?.trim();
  return label || "____";
};

const formatFillInBlankPreviewSegments = (
  item: Record<string, unknown>,
): QuestionPreviewSegment[] => {
  const template =
    typeof item.template === "string" ? item.template.trim() : "";
  if (!template) {
    return [];
  }

  const answers = normalizeAnswersMap(item.answers);
  const options = normalizeOptions(item.options);
  const blanks = normalizeBlanks(item.blanks);
  const optionById = new Map(options.map((option) => [option.id, option.text]));
  const segments: QuestionPreviewSegment[] = [];
  let lastIndex = 0;
  const tokenPattern = new RegExp(FILL_IN_BLANK_TEMPLATE_TOKEN.source, "g");
  let match: RegExpExecArray | null;
  let hadToken = false;

  while ((match = tokenPattern.exec(template)) !== null) {
    hadToken = true;
    if (match.index > lastIndex) {
      segments.push({ text: template.slice(lastIndex, match.index) });
    }

    const blankId = match[1];
    const answerValue = answers[blankId];
    if (!answerValue) {
      segments.push({ text: resolveBlankFallbackLabel(blankId, blanks) });
    } else {
      segments.push({
        text: resolveFillInBlankInsertedText(answerValue, optionById),
        bold: true,
      });
    }

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < template.length) {
    segments.push({ text: template.slice(lastIndex) });
  }

  if (!hadToken) {
    return [{ text: template }];
  }

  return segments;
};

export const buildFillInBlankUserAnswerSegments = (
  template: string,
  chosenAnswer: string,
): QuestionPreviewSegment[] => {
  const trimmedTemplate = template.trim();
  const trimmedAnswer = chosenAnswer.trim();

  if (!trimmedTemplate.includes("{{")) {
    return [{ text: trimmedTemplate || trimmedAnswer }];
  }
  if (!trimmedAnswer) {
    return formatFillInBlankPreviewSegments({ template: trimmedTemplate });
  }

  const segments: QuestionPreviewSegment[] = [];
  const tokenPattern = new RegExp(FILL_IN_BLANK_TEMPLATE_TOKEN.source, "g");
  let templateCursor = 0;
  let answerCursor = 0;
  let match: RegExpExecArray | null;

  while ((match = tokenPattern.exec(trimmedTemplate)) !== null) {
    const literalBefore = trimmedTemplate.slice(templateCursor, match.index);
    if (literalBefore) {
      if (!trimmedAnswer.startsWith(literalBefore, answerCursor)) {
        return [{ text: trimmedAnswer }];
      }
      segments.push({ text: literalBefore });
      answerCursor += literalBefore.length;
    }

    const templateAfter = trimmedTemplate.slice(match.index + match[0].length);
    const nextLiteral = /^[\s\S]*?(?=\{\{|$)/.exec(templateAfter)?.[0] ?? "";

    if (nextLiteral) {
      const nextPos = trimmedAnswer.indexOf(nextLiteral, answerCursor);
      if (nextPos < answerCursor) {
        return [{ text: trimmedAnswer }];
      }
      const inserted = trimmedAnswer.slice(answerCursor, nextPos);
      if (inserted) {
        segments.push({ text: inserted, bold: true });
      }
      answerCursor = nextPos;
    } else {
      const inserted = trimmedAnswer.slice(answerCursor);
      if (inserted) {
        segments.push({ text: inserted, bold: true });
      }
      answerCursor = trimmedAnswer.length;
    }

    templateCursor = match.index + match[0].length;
  }

  const tailLiteral = trimmedTemplate.slice(templateCursor);
  if (tailLiteral) {
    if (trimmedAnswer.slice(answerCursor) === tailLiteral) {
      segments.push({ text: tailLiteral });
    } else if (answerCursor < trimmedAnswer.length) {
      segments.push({ text: trimmedAnswer.slice(answerCursor) });
    }
  }

  return segments.length > 0 ? segments : [{ text: trimmedAnswer }];
};

export const resolveInteractiveQuestionPreviewContent = (
  item: Record<string, unknown>,
): QuestionPreviewSegment[] => {
  const itemType = item.type ?? item.question_type;
  if (typeof itemType !== "string") {
    return [];
  }

  const title = readQuestionField(item, "title");
  const instruction = readQuestionField(item, "instruction");
  const segments: QuestionPreviewSegment[] = [];

  switch (itemType) {
    case "fill_in_blank":
      appendPlainPreviewPart(segments, title);
      appendPlainPreviewPart(segments, instruction);
      if (segments.length) {
        segments.push(PREVIEW_PART_BREAK);
      }
      segments.push(...formatFillInBlankPreviewSegments(item));
      break;
    case "ordering":
    case "ordered_response":
      appendPlainPreviewPart(segments, title);
      appendPlainPreviewPart(segments, instruction);
      appendPlainPreviewPart(segments, readQuestionField(item, "prompt"));
      break;
    case "matching":
      appendPlainPreviewPart(segments, title);
      appendPlainPreviewPart(segments, instruction);
      appendPlainPreviewPart(
        segments,
        formatOptionListPreview("Left", item.left),
      );
      appendPlainPreviewPart(
        segments,
        formatOptionListPreview("Right", item.right),
      );
      break;
    default:
      break;
  }

  return segments;
};

export const resolveInteractiveQuestionPreviewText = (
  item: Record<string, unknown>,
): string =>
  resolveInteractiveQuestionPreviewContent(item)
    .map((segment) => segment.text)
    .join("");

const hasUnresolvedFillInBlankTokens = (text: string): boolean =>
  /\{\{\w+\}\}/.test(text);

export const resolveDashboardQuestionDisplayText = (
  ref: DashboardQuestionRef,
): string => {
  if (typeof ref === "string") {
    const trimmed = ref.trim();
    if (trimmed.includes("{{")) {
      return resolveInteractiveQuestionPreviewText({
        type: "fill_in_blank",
        template: trimmed,
      });
    }
    return trimmed;
  }
  if (!ref || typeof ref !== "object") {
    return "";
  }

  const interactivePreview = resolveInteractiveQuestionPreviewText(ref);
  if (interactivePreview && !hasUnresolvedFillInBlankTokens(interactivePreview)) {
    return interactivePreview;
  }

  const extracted = extractAssessmentQuestion(ref);
  if (extracted && !hasUnresolvedFillInBlankTokens(extracted)) {
    return extracted;
  }

  if (interactivePreview) {
    return interactivePreview;
  }

  return extracted || "";
};

export const resolveDashboardQuestionPreviewContent = (
  ref: DashboardQuestionRef,
): QuestionPreviewSegment[] => {
  if (typeof ref === "string") {
    const trimmed = ref.trim();
    if (!trimmed) {
      return [];
    }
    if (trimmed.includes("{{")) {
      return resolveInteractiveQuestionPreviewContent({
        type: "fill_in_blank",
        template: trimmed,
      });
    }
    return [{ text: trimmed }];
  }
  if (!ref || typeof ref !== "object") {
    return [];
  }

  const interactivePreview = resolveInteractiveQuestionPreviewContent(ref);
  if (interactivePreview.length) {
    const joined = interactivePreview.map((segment) => segment.text).join("");
    if (!hasUnresolvedFillInBlankTokens(joined)) {
      return interactivePreview;
    }
  }

  const extracted = extractAssessmentQuestion(ref);
  if (extracted && !hasUnresolvedFillInBlankTokens(extracted)) {
    return [{ text: extracted }];
  }

  if (interactivePreview.length) {
    return interactivePreview;
  }

  if (typeof ref.question === "string" && ref.question.trim()) {
    return [{ text: ref.question.trim() }];
  }

  return [];
};

// Human-readable question text for UI previews (never raw {{b1}} tokens).
export const resolveAssessmentQuestionPreviewContent = (
  question: AssessmentQuestion,
): QuestionPreviewSegment[] => {
  const raw = assessmentQuestionToRaw(question);
  const interactive = resolveInteractiveQuestionPreviewContent(raw);
  if (interactive.length) {
    const joined = interactive.map((segment) => segment.text).join("");
    if (!hasUnresolvedFillInBlankTokens(joined)) {
      return interactive;
    }
  }

  const extracted = extractAssessmentQuestion(raw);
  if (extracted && !hasUnresolvedFillInBlankTokens(extracted)) {
    return [{ text: extracted }];
  }

  if (interactive.length) {
    return interactive;
  }

  const fallback =
    question.question?.trim() || question.title?.trim() || "";
  return fallback ? [{ text: fallback }] : [];
};

export const resolveAssessmentQuestionPreviewText = (
  question: AssessmentQuestion,
): string =>
  resolveAssessmentQuestionPreviewContent(question)
    .map((segment) => segment.text)
    .join("");

// Segment-aware fill-in-blank / matching answer formatting for UI.
export const formatInteractiveChosenAnswerSegments = (
  question: AssessmentQuestion,
  chosenAnswer: string,
): QuestionPreviewSegment[] => {
  const trimmed = chosenAnswer.trim();
  if (!trimmed) {
    return [];
  }

  let payload: unknown = trimmed;
  if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
    try {
      payload = JSON.parse(trimmed);
    } catch {
      return [{ text: trimmed }];
    }
  }

  const questionType = getAssessmentQuestionType(question);

  if (
    questionType === "fill_in_blank" &&
    payload &&
    typeof payload === "object" &&
    !Array.isArray(payload) &&
    (payload as { user_answers?: unknown }).user_answers &&
    typeof (payload as { user_answers?: unknown }).user_answers === "object"
  ) {
    const userAnswers = normalizeAnswersMap(
      (payload as { user_answers: unknown }).user_answers,
    );
    const template = question.template?.trim() || "";
    if (template.includes("{{")) {
      return formatFillInBlankPreviewSegments({
        template,
        answers: userAnswers,
        options: question.options,
        blanks: question.blanks,
      });
    }
  }

  // If chosen_answer is already a filled sentence (history / backend format),
  // bold the inserted words against the template.
  if (
    questionType === "fill_in_blank" &&
    typeof payload === "string" &&
    question.template?.includes("{{")
  ) {
    return buildFillInBlankUserAnswerSegments(question.template, payload);
  }

  if (
    questionType === "fill_in_blank" &&
    typeof trimmed === "string" &&
    question.template?.includes("{{") &&
    !trimmed.startsWith("{")
  ) {
    return buildFillInBlankUserAnswerSegments(question.template, trimmed);
  }

  const plain = formatInteractiveChosenAnswerDisplay(question, chosenAnswer);
  return plain ? [{ text: plain }] : [];
};

const resolveOptionText = (
  optionId: string,
  options: Array<{ id: string; text: string }> | undefined,
): string => {
  const found = options?.find((option) => option.id === optionId);
  return found?.text?.trim() || optionId;
};

// Format interactive payload / chosen_answer JSON for feedback display.
export const formatInteractiveChosenAnswerDisplay = (
  question: AssessmentQuestion,
  chosenAnswer: string,
): string => {
  const trimmed = chosenAnswer.trim();
  if (!trimmed) {
    return "";
  }

  let payload: unknown = trimmed;
  if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
    try {
      payload = JSON.parse(trimmed);
    } catch {
      return trimmed;
    }
  }

  const questionType = getAssessmentQuestionType(question);

  if (
    questionType === "matching" &&
    payload &&
    typeof payload === "object" &&
    !Array.isArray(payload) &&
    Array.isArray((payload as { user_pairs?: unknown }).user_pairs)
  ) {
    const pairs = (payload as {
      user_pairs: Array<{ left_id?: string; right_id?: string }>;
    }).user_pairs;
    const lines = pairs
      .map((pair) => {
        const left = resolveOptionText(String(pair.left_id ?? ""), question.left);
        const right = resolveOptionText(
          String(pair.right_id ?? ""),
          question.right,
        );
        if (!left && !right) return "";
        return `${left} → ${right}`;
      })
      .filter(Boolean);
    return lines.join("\n") || trimmed;
  }

  if (
    questionType === "fill_in_blank" &&
    payload &&
    typeof payload === "object" &&
    !Array.isArray(payload) &&
    (payload as { user_answers?: unknown }).user_answers &&
    typeof (payload as { user_answers?: unknown }).user_answers === "object"
  ) {
    const userAnswers = normalizeAnswersMap(
      (payload as { user_answers: unknown }).user_answers,
    );
    const template = question.template?.trim() || "";
    if (template.includes("{{")) {
      const segments = formatFillInBlankPreviewSegments({
        template,
        answers: userAnswers,
        options: question.options,
        blanks: question.blanks,
      });
      return segments.map((segment) => segment.text).join("") || trimmed;
    }
  }

  if (
    (questionType === "ordering" || questionType === "ordered_response") &&
    payload &&
    typeof payload === "object" &&
    !Array.isArray(payload) &&
    Array.isArray((payload as { user_order?: unknown }).user_order)
  ) {
    const order = (payload as { user_order: string[] }).user_order;
    return order
      .map((optionId, index) => {
        const text = resolveOptionText(optionId, question.options);
        return `${index + 1}. ${text}`;
      })
      .join("\n");
  }

  return trimmed;
};

export const isMultipleChoiceAnswers = (
  answers: AssessmentQuestion["answers"],
): answers is Answer[] =>
  Array.isArray(answers) &&
  answers.every(
    (item) =>
      item &&
      typeof item === "object" &&
      typeof (item as Answer).text === "string",
  );

const getFillInBlankCorrectAnswers = (
  question: AssessmentQuestion,
): Record<string, string> | undefined => {
  if (
    question.answers &&
    !Array.isArray(question.answers) &&
    typeof question.answers === "object"
  ) {
    const map = normalizeAnswersMap(question.answers);
    return Object.keys(map).length > 0 ? map : undefined;
  }
  return undefined;
};

const getOrderingCorrectAnswers = (
  question: AssessmentQuestion,
): string[] | undefined => {
  if (Array.isArray(question.answers) && !isMultipleChoiceAnswers(question.answers)) {
    return normalizeStringArray(question.answers);
  }
  return undefined;
};

const getMatchingCorrectAnswers = (
  question: AssessmentQuestion,
): Record<string, string> | undefined => {
  if (
    question.answers &&
    !Array.isArray(question.answers) &&
    typeof question.answers === "object"
  ) {
    const map = normalizeAnswersMap(question.answers);
    return Object.keys(map).length > 0 ? map : undefined;
  }
  return undefined;
};

export const assessmentQuestionToContentBlock = (
  question: AssessmentQuestion,
  options?: {
    state?: "default" | "disable";
    userAnswers?: Record<string, string>;
    userOrder?: string[];
    userPairs?: Array<{ left_id: string; right_id: string }>;
    isCorrect?: boolean;
    feedback?: string;
  },
): ContentBlock | null => {
  const questionType = getAssessmentQuestionType(question);
  if (!isInteractiveAssessmentType(questionType)) {
    return null;
  }

  const state = options?.state ?? "default";
  const title = question.title || question.question;
  const instruction = question.instruction;
  const prompt = question.prompt;

  if (questionType === "fill_in_blank") {
    const correctAnswers = getFillInBlankCorrectAnswers(question);
    const block: FillInBlankContentBlock = {
      type: "fill_in_blank",
      title,
      instruction,
      template: question.template ?? "",
      blanks: normalizeBlanks(question.blanks),
      options: normalizeOptions(question.options),
      state,
      ...(typeof options?.isCorrect === "boolean"
        ? { is_correct: options.isCorrect }
        : {}),
      ...(options?.userAnswers ? { user_answers: options.userAnswers } : {}),
      ...(correctAnswers ? { answers: correctAnswers } : {}),
      ...(options?.feedback ? { feedback: options.feedback } : {}),
    };
    return block;
  }

  if (questionType === "ordering") {
    const correctAnswers = getOrderingCorrectAnswers(question);
    const block: OrderingContentBlock = {
      type: "ordering",
      title,
      instruction,
      prompt,
      slot_count:
        typeof question.slot_count === "number"
          ? question.slot_count
          : Number(question.slot_count) || 0,
      options: normalizeOptions(question.options),
      state,
      ...(typeof options?.isCorrect === "boolean"
        ? { is_correct: options.isCorrect }
        : {}),
      ...(options?.userOrder ? { user_order: options.userOrder } : {}),
      ...(correctAnswers ? { answers: correctAnswers } : {}),
      ...(options?.feedback ? { feedback: options.feedback } : {}),
    };
    return block;
  }

  if (questionType === "ordered_response") {
    const correctAnswers = getOrderingCorrectAnswers(question);
    const block: OrderedResponseContentBlock = {
      type: "ordered_response",
      title,
      instruction,
      prompt,
      slot_count:
        typeof question.slot_count === "number"
          ? question.slot_count
          : Number(question.slot_count) || 0,
      options: normalizeOptions(question.options),
      state,
      ...(typeof options?.isCorrect === "boolean"
        ? { is_correct: options.isCorrect }
        : {}),
      ...(options?.userOrder ? { user_order: options.userOrder } : {}),
      ...(correctAnswers ? { answers: correctAnswers } : {}),
      ...(options?.feedback ? { feedback: options.feedback } : {}),
    };
    return block;
  }

  const correctAnswers = getMatchingCorrectAnswers(question);
  const block: MatchingContentBlock = {
    type: "matching",
    title,
    instruction,
    left: normalizeOptions(question.left),
    right: normalizeOptions(question.right),
    state,
    ...(typeof options?.isCorrect === "boolean"
      ? { is_correct: options.isCorrect }
      : {}),
    ...(options?.userPairs ? { user_pairs: options.userPairs } : {}),
    ...(correctAnswers ? { answers: correctAnswers } : {}),
    ...(options?.feedback ? { feedback: options.feedback } : {}),
  };
  return block;
};

export const serializeAssessmentInteractiveAnswer = (
  payload: InteractionSubmitPayload,
  question?: AssessmentQuestion,
): string => {
  // Backend stores fill-in-blank chosen_answer as the filled sentence.
  if (question && getAssessmentQuestionType(question) === "fill_in_blank") {
    const display = formatInteractiveChosenAnswerDisplay(
      question,
      JSON.stringify(payload),
    );
    if (display.trim()) {
      return display;
    }
  }
  return JSON.stringify(payload);
};

export const assessmentQuestionToAnswerBlock = (
  question: AssessmentQuestion,
  questionIndex: number,
  chosenAnswer?: string,
): AssessmentQuestionAnswerBlock => {
  const questionType = getAssessmentQuestionType(question);

  const block: AssessmentQuestionAnswerBlock = {
    type: questionType,
    question_index: question.question_index ?? questionIndex,
    ...(typeof question.id === "string" && question.id.trim()
      ? { question_id: question.id.trim() }
      : {}),
    competency: question.competency,
    language: question.language,
    title: question.title,
    instruction: question.instruction,
    prompt: question.prompt,
    template: question.template,
    blanks: question.blanks,
    options: question.options,
    slot_count: question.slot_count,
    left: question.left,
    right: question.right,
    scoring_rubric: question.scoring_rubric,
    grading_guidance: question.grading_guidance,
    ...(chosenAnswer !== undefined ? { chosen_answer: chosenAnswer } : {}),
  };

  if (question.answers !== undefined) {
    block.answers = question.answers;
  }

  return block;
};

export type AssessmentSendAnswerOptions = {
  interactivePayload?: InteractionSubmitPayload;
  questionBlock?: AssessmentQuestionAnswerBlock;
};

export const buildAssessmentAnswerRequest = (
  assessmentId: string,
  answer: AssessmentAnswerItem,
  meta: {
    total_questions_number?: number;
    answered_questions_number: number;
    scores_earned: number;
    time_left?: number | null;
    time_spent?: number;
    is_submitted?: boolean;
    save_id?: string;
    visit_id?: string;
    segment_id?: string;
    expected_revision?: number;
    protocol_version?: number;
    question_id?: string;
  },
  options?: AssessmentSendAnswerOptions,
): AssessmentAnswerPayload => {
  const questionId =
    (typeof meta.question_id === "string" && meta.question_id.trim()
      ? meta.question_id.trim()
      : null) ||
    (typeof answer.question_id === "string" && answer.question_id.trim()
      ? answer.question_id.trim()
      : null) ||
    (typeof options?.questionBlock?.question_id === "string" &&
    options.questionBlock.question_id.trim()
      ? options.questionBlock.question_id.trim()
      : null);

  const answers = options?.questionBlock
    ? [
        questionId
          ? { ...options.questionBlock, question_id: questionId }
          : options.questionBlock,
      ]
    : [questionId ? { ...answer, question_id: questionId } : answer];

  return {
    assessment_id: assessmentId,
    answers,
    ...(options?.interactivePayload
      ? { payload: options.interactivePayload }
      : {}),
    total_questions_number: meta.total_questions_number,
    answered_questions_number: meta.answered_questions_number,
    scores_earned: meta.scores_earned,
    time_left: meta.time_left,
    time_spent: meta.time_spent,
    ...(typeof meta.is_submitted === "boolean"
      ? { is_submitted: meta.is_submitted }
      : {}),
    ...(typeof meta.save_id === "string" ? { save_id: meta.save_id } : {}),
    ...(typeof meta.visit_id === "string" ? { visit_id: meta.visit_id } : {}),
    ...(typeof meta.segment_id === "string"
      ? { segment_id: meta.segment_id }
      : {}),
    ...(typeof meta.expected_revision === "number"
      ? { expected_revision: meta.expected_revision }
      : {}),
    ...(typeof meta.protocol_version === "number"
      ? { protocol_version: meta.protocol_version }
      : {}),
  };
};

export const getAssessmentQuestionMaxScore = (
  question: AssessmentQuestion,
): number => {
  if (typeof question.max_score === "number" && question.max_score > 0) {
    return question.max_score;
  }
  if (isMsqAssessmentType(getAssessmentQuestionType(question))) {
    return isMultipleChoiceAnswers(question.answers)
      ? question.answers.reduce((max, answer) => Math.max(max, answer.points), 0)
      : 0;
  }
  return 5;
};
